import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  Users,
  CheckSquare,
  BarChart3,
  Briefcase,
  UserCog,
  ChevronDown,
  ChevronRight,
  LogOut,
  Menu,
  X,
  Building2,
  Wallet,
  Tag,
  Layers,
  Contact,
  ArrowLeftRight,
  TrendingUp,
  LifeBuoy,
  PieChart,
  FolderOpen,
  History,
  Package,
  CreditCard,
  Stethoscope,
  Receipt,
  Banknote,
  HeartPulse,
  Settings,
  Activity,
  Shield,
  MessageSquare,
  Repeat,
  Split,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '@/app/providers/AuthProvider';
import { useRole } from '@/hooks/useRole';
import { ClientSwitcher } from '@/components/ClientSwitcher';
import { TicketButton } from '@/features/tickets/TicketButton';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface NavLeaf {
  kind: 'leaf';
  to: string;
  label: string;
  icon: LucideIcon;
  managerOnly?: boolean;
}

interface NavGroup {
  kind: 'group';
  id: string;
  label: string;
  icon: LucideIcon;
  managerOnly?: boolean;
  items: NavLeaf[];
}

type NavEntry = NavLeaf | NavGroup;

const navItems: NavEntry[] = [
  { kind: 'leaf', to: '/app/meu-dia', label: 'Meu Dia', icon: CalendarDays },
  { kind: 'leaf', to: '/app/clientes', label: 'Clientes', icon: Building2 },

  {
    kind: 'group',
    id: 'financeiro',
    label: 'Financeiro',
    icon: Banknote,
    items: [
      { kind: 'leaf', to: '/app/transacoes', label: 'Transações', icon: ArrowLeftRight },
      { kind: 'leaf', to: '/app/conciliacao', label: 'Conciliação', icon: Repeat },
      { kind: 'leaf', to: '/app/fluxo-de-caixa', label: 'Fluxo de Caixa', icon: TrendingUp },
      { kind: 'leaf', to: '/app/dre', label: 'DRE', icon: PieChart },
      { kind: 'leaf', to: '/app/resultado-por-canal', label: 'Resultado por Canal', icon: Split },
    ],
  },
  {
    kind: 'group',
    id: 'cadastros',
    label: 'Cadastros',
    icon: Settings,
    items: [
      { kind: 'leaf', to: '/app/contas', label: 'Contas', icon: Wallet },
      { kind: 'leaf', to: '/app/categorias', label: 'Categorias', icon: Tag },
      { kind: 'leaf', to: '/app/centros-de-custo', label: 'C. de Custo', icon: Layers },
      { kind: 'leaf', to: '/app/contrapartes', label: 'Forn./Clientes', icon: Contact },
    ],
  },
  {
    kind: 'group',
    id: 'cobranca',
    label: 'Cobrança',
    icon: Receipt,
    items: [
      { kind: 'leaf', to: '/app/boletos', label: 'Boletos', icon: Receipt },
      { kind: 'leaf', to: '/app/convenios', label: 'Convênios', icon: Stethoscope },
      { kind: 'leaf', to: '/app/cartoes', label: 'Cartões', icon: CreditCard },
    ],
  },
  {
    kind: 'group',
    id: 'pacientes',
    label: 'Pacientes',
    icon: HeartPulse,
    items: [
      { kind: 'leaf', to: '/app/pacotes', label: 'Pacotes', icon: Package },
      { kind: 'leaf', to: '/app/documentos', label: 'Documentos', icon: FolderOpen },
      { kind: 'leaf', to: '/app/timeline', label: 'Linha do tempo', icon: History },
    ],
  },
  {
    kind: 'group',
    id: 'operacao',
    label: 'Operação',
    icon: Activity,
    items: [
      { kind: 'leaf', to: '/app/tarefas', label: 'Tarefas', icon: CheckSquare },
      { kind: 'leaf', to: '/app/comercial', label: 'Comercial', icon: Briefcase },
      { kind: 'leaf', to: '/app/mensagens', label: 'Mensagens', icon: MessageSquare },
      { kind: 'leaf', to: '/app/relatorios', label: 'Relatórios', icon: BarChart3 },
    ],
  },
  {
    kind: 'group',
    id: 'admin',
    label: 'Admin',
    icon: Shield,
    managerOnly: true,
    items: [
      { kind: 'leaf', to: '/app/colaboradores', label: 'Colaboradores', icon: UserCog },
      { kind: 'leaf', to: '/app/gestao', label: 'Gestão Geral', icon: Users },
      { kind: 'leaf', to: '/app/playbooks', label: 'Playbooks', icon: Repeat },
      { kind: 'leaf', to: '/app/implementacoes', label: 'Implementações', icon: LifeBuoy },
    ],
  },
];

const roleLabels: Record<string, string> = {
  admin_clinance: 'Administrador',
  gerente_operacao: 'Gerente de Operações',
  consultor: 'Consultor',
  comercial: 'Comercial',
};

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0] ?? '')
    .join('')
    .toUpperCase();
}

function findGroupForPath(pathname: string): string | null {
  for (const entry of navItems) {
    if (entry.kind !== 'group') continue;
    if (entry.items.some((it) => pathname.startsWith(it.to))) return entry.id;
  }
  return null;
}

function leafLink(item: NavLeaf, onClose: (() => void) | undefined, indented: boolean) {
  return (
    <NavLink
      key={item.to}
      to={item.to}
      onClick={onClose}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg py-2 text-sm font-medium transition-colors ${
          indented ? 'pl-9 pr-3' : 'px-3'
        } ${
          isActive ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
        }`
      }
    >
      <item.icon size={indented ? 14 : 18} className="flex-shrink-0" />
      {item.label}
    </NavLink>
  );
}

function SidebarContent({ onClose }: { onClose?: () => void }) {
  const { profile, signOut } = useAuth();
  const { isManager } = useRole();
  const navigate = useNavigate();
  const location = useLocation();

  const visibleNav = navItems.filter((entry) => (entry.managerOnly ? isManager : true));

  // Auto-expande o grupo da rota atual; mantém o conjunto enquanto o usuário navega.
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    const active = findGroupForPath(location.pathname);
    return new Set(active ? [active] : []);
  });

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    const active = findGroupForPath(location.pathname);
    if (active) {
      setOpenGroups((prev) => {
        if (prev.has(active)) return prev;
        const next = new Set(prev);
        next.add(active);
        return next;
      });
    }
  }, [location.pathname]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-white/10 px-6 py-6">
        <img
          src="/identidade-visual/logo/vetor/logo-horizontal-branco.svg"
          alt="Clinance"
          className="h-8 w-auto"
        />
        {onClose && (
          <button
            className="text-white/70 hover:text-white lg:hidden"
            onClick={onClose}
            aria-label="Fechar menu"
          >
            <X size={20} />
          </button>
        )}
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
        {visibleNav.map((entry) => {
          if (entry.kind === 'leaf') {
            return leafLink(entry, onClose, false);
          }
          const isOpen = openGroups.has(entry.id);
          const hasActive = entry.items.some((it) => location.pathname.startsWith(it.to));
          const Icon = entry.icon;
          return (
            <div key={entry.id}>
              <button
                type="button"
                onClick={() => toggleGroup(entry.id)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  hasActive ? 'text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
                aria-expanded={isOpen}
              >
                <Icon size={18} className="flex-shrink-0" />
                <span className="flex-1 text-left">{entry.label}</span>
                {isOpen ? (
                  <ChevronDown size={14} className="flex-shrink-0 text-white/50" />
                ) : (
                  <ChevronRight size={14} className="flex-shrink-0 text-white/50" />
                )}
              </button>
              {isOpen && (
                <div className="mt-0.5 space-y-0.5">
                  {entry.items.map((it) => leafLink(it, onClose, true))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="border-t border-white/10 px-3 py-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-white/10">
              <Avatar className="h-8 w-8 flex-shrink-0">
                <AvatarFallback className="bg-accent text-xs font-bold text-accent-foreground">
                  {getInitials(profile?.name || 'U')}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-white">
                  {profile?.name || 'Usuário'}
                </p>
                <p className="truncate text-xs text-white/50">
                  {profile?.role ? (roleLabels[profile.role] ?? profile.role) : ''}
                </p>
              </div>
              <ChevronDown size={14} className="flex-shrink-0 text-white/50" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleSignOut}
              className="text-destructive focus:text-destructive"
            >
              <LogOut size={14} className="mr-2" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-full bg-secondary">
      <aside className="hidden w-64 flex-shrink-0 bg-primary lg:flex lg:flex-col">
        <SidebarContent />
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 z-40 flex lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="relative z-10 flex w-64 flex-col bg-primary">
            <SidebarContent onClose={() => setSidebarOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-border bg-white px-4 py-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-muted-foreground hover:text-foreground lg:hidden"
            aria-label="Abrir menu"
          >
            <Menu size={22} />
          </button>
          <img
            src="/identidade-visual/logo/vetor/logo-horizontal.svg"
            alt="Clinance"
            className="h-7 w-auto lg:hidden"
          />
          <div className="hidden lg:block">
            <ClientSwitcher />
          </div>
          <div className="ml-auto">
            <TopbarTicketButton />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function TopbarTicketButton() {
  const { clientId } = useCurrentClient();
  return <TicketButton variant="topbar" clientId={clientId ?? null} />;
}
